import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { EventsContext } from "../services/EventsContext";
import { Helmet } from "react-helmet-async";

import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import SVGSeparator from "../services/SVGSeparator";
import background from "../assets/img/chatgptpdv.png";

const mapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
};

const defaultCenter = { lat: 46.603354, lng: 1.888334 };

// Icônes par catégorie
const categoryIcons = {
  supermarche: "https://cdn-icons-png.flaticon.com/128/2331/2331970.png",
  restaurant: "https://cdn-icons-png.flaticon.com/128/1046/1046784.png",
  cave: "https://cdn-icons-png.flaticon.com/128/2969/2969202.png",
  default: "https://cdn-icons-png.flaticon.com/128/684/684908.png",
};

// ——— Helpers ———
const n = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();



const buildFullAddress = (p) => {
  // Nouveau schéma
  if (p.address1 || p.postalCode || p.country) {
    return [
      p.address1,
      p.address2,
      [p.postalCode, p.city].filter(Boolean).join(" "),
      p.country || "France",
    ]
      .filter(Boolean)
      .join(", ");
  }
  // Ancien schéma
  return [p.address, p.city, "France"].filter(Boolean).join(", ");
};

const pickBestFR = (results, postalCode, city) => {
  if (!results?.length) return null;
  const get = (r, type) =>
    r.address_components.find((c) => c.types.includes(type))?.long_name || "";
  // 1. match CP + ville
  const both = results.find(
    (r) =>
      get(r, "postal_code") === postalCode &&
      n(get(r, "locality") || get(r, "postal_town")) === n(city)
  );
  if (both) return both;
  // 2. match CP
  const byCP = results.find((r) => get(r, "postal_code") === postalCode);
  if (byCP) return byCP;
  // 3. match ville
  const byCity = results.find(
    (r) => n(get(r, "locality") || get(r, "postal_town")) === n(city)
  );
  return byCity || results[0];
};

const FR_BOUNDS = { sw: { lat: 41.0, lng: -5.5 }, ne: { lat: 51.5, lng: 9.7 } };
const inFR = (lat, lng) =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  lat >= FR_BOUNDS.sw.lat &&
  lat <= FR_BOUNDS.ne.lat &&
  lng >= FR_BOUNDS.sw.lng &&
  lng <= FR_BOUNDS.ne.lng;

const SalesPointsMap = () => {
  const { salesPoints } = useContext(EventsContext);
  const { t } = useTranslation();

  const mapRef = useRef(null);

  const [search, setSearch] = useState("");
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [geocodedPoints, setGeocodedPoints] = useState([]);

  // Charge la carte via Google Maps API
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyCZKd9GsscBfVyRmXv1azAVbTlh0qPnfGc",
    language: "fr",
    region: "FR",
  });

  useEffect(() => {
    if (!isLoaded || !salesPoints?.length) return;

    const geocoder = new window.google.maps.Geocoder();

    Promise.all(
      salesPoints.map((point) => {
        // 1) Si lat/lng valides → on les conserve
        if (inFR(point.lat, point.lng)) {
          return Promise.resolve({
            ...point,
            category: point.category || "default",
          });
        }

        // 2) Sinon, on géocode en France
        const address = buildFullAddress(point);

        return new Promise((resolve) => {
          geocoder.geocode(
            { address, componentRestrictions: { country: "FR" } },
            (results, status) => {
              if (status === "OK" && results?.length) {
                const best = pickBestFR(results, point.postalCode, point.city);
                const loc = (best?.geometry || results[0].geometry).location;
                resolve({
                  ...point,
                  lat: loc.lat(),
                  lng: loc.lng(),
                  category: point.category || "default",
                });
              } else {
                console.warn(
                  `Geocode KO (${status}) pour:`,
                  address,
                  "→ point ignoré pour la carte"
                );
                resolve({ ...point, category: point.category || "default" });
              }
            }
          );
        });
      })
    ).then((points) => {
      setGeocodedPoints(points);

      // Fit bounds sur les points valides
      const usable = points.filter((p) => inFR(p.lat, p.lng));
      if (usable.length && mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        usable.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        mapRef.current.fitBounds(bounds);
      }
    });
  }, [isLoaded, salesPoints]);


  useEffect(() => {
    const q = fold(search);
    if (!q) {
      setFiltered(geocodedPoints);
      return;
    }
    const tokens = q.split(/\s+/).filter(Boolean); // ex. "guimety 84340"
    const out = (geocodedPoints || []).filter((p) => {
      const h = hay(p);
      return tokens.every((t) => h.includes(t));
    });
    setFiltered(out);
  }, [search, geocodedPoints]);

// helper robuste (accents → sans accents, lowercase)
const fold = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

    

// calcule la pile de texte searchable d’un point
const hay = (p) =>
  fold(
    [
      p.name,
      p.address1,
      p.address2,
      p.address,            // compat ancien schéma
      p.formattedAddress,
      p.postalCode,
      p.city,
      p.country,
    ]
      .filter(Boolean)
      .join(" ")
  );

// ⬇️ AJOUTE cet état à côté de tes autres useState
const [filtered, setFiltered] = useState([]);

// Filtrage accent-insensible, multi-mots, sans useMemo
const tokens = fold(search).split(/\s+/).filter(Boolean);

const filteredPoints =
  tokens.length === 0
    ? geocodedPoints
    : geocodedPoints.filter((p) => {
        const haystack = fold(
          [
            p.name,
            p.address1,
            p.address2,
            p.address,            // ancien schéma
            p.formattedAddress,
            p.postalCode,
            p.city,
            p.country,
          ]
            .filter(Boolean)
            .join(" ")
        );
        return tokens.every((tok) => haystack.includes(tok));
      });


// ensuite SEULEMENT :
if (!isLoaded) return (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FBF7EF' }}>
    <p className="font-nunito text-stone-500">Chargement de la carte…</p>
  </div>
);

  return (
    <>
      <Helmet>
        <title>Nos Points de Vente | Au Coing du Ventoux</title>
        <meta
          name="description"
          content="Trouvez un point de vente près de chez vous pour acheter notre alcool de coing artisanal."
        />
        <meta
          property="og:title"
          content="Nos Points de Vente | Au Coing du Ventoux"
        />
        <meta property="og:url" content="https://aucoingduventoux.com/sales" />
        <link rel="canonical" href="https://aucoingduventoux.com/sales" />
      </Helmet>

      {/* Section Héro */}
      <div className="relative h-screen min-h-[560px] overflow-hidden">
        <img
          src={background}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(30,20,8,0.6) 0%, rgba(30,20,8,0.45) 45%, rgba(30,20,8,0.7) 100%)' }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 60%)' }}
        />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <h1
            className="font-sans text-4xl md:text-5xl lg:text-6xl leading-tight max-w-4xl"
            style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}
          >
            {t("sales.bigtitle")}
          </h1>
          <SVGSeparator width={120} height={50} className="" />
          <p
            className="mt-4 font-nunito text-lg md:text-xl max-w-2xl text-stone-100/90"
            style={{ textShadow: '0 1px 10px rgba(0,0,0,0.6)' }}
          >
            {t("sales.bigdescription")}
          </p>
        </div>
      </div>

      {/* Intro */}
      <div style={{ backgroundColor: '#FBF7EF' }} className="pt-16 pb-4">
        <div className="text-center max-w-3xl mx-auto px-6">
          
          <h2 className="font-sans mt-4 text-3xl md:text-4xl text-stone-900">
            {t("sales.title")}
          </h2>
          
          <SVGSeparator width={120} height={50} className="mx-auto" />
        </div>
      </div>

      <div style={{ backgroundColor: '#FBF7EF' }} className="flex flex-col md:flex-row gap-6 px-6 md:px-10 pb-20">
        {/* Liste et recherche */}
        <div className="w-full md:w-1/3">
          <input
            type="text"
            placeholder="Rechercher un point de vente..."
            className="w-full rounded-xl border bg-white px-4 py-3 mb-4 font-nunito text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="space-y-3 max-h-[420px] overflow-auto pr-1">
            {filtered.map((point) => (
              <li
                key={point.id}
                className={`p-4 rounded-2xl border bg-white cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selectedPoint?.id === point.id ? 'shadow-md' : ''
                }`}
                style={{ borderColor: selectedPoint?.id === point.id ? 'rgba(217,166,65,0.6)' : 'rgba(120,90,40,0.15)' }}
                onClick={() => setSelectedPoint(point)}
              >
                <h3 className="font-sans text-lg text-stone-900">{point.name}</h3>
                <p className="font-nunito text-sm text-stone-500 mt-1">
                  {point.address ||
                    [point.address1, point.address2].filter(Boolean).join(", ")}
                </p>
                <p className="font-nunito text-sm text-stone-500">
                  {point.postalCode
                    ? `${point.postalCode} ${point.city || ""}`
                    : point.city}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Carte interactive */}
        <div className="w-full md:w-2/3 h-[50vh] md:h-[620px]">
          <div className="w-full h-full rounded-2xl overflow-hidden border shadow-sm" style={{ borderColor: 'rgba(120,90,40,0.15)' }}>
            <GoogleMap
              onLoad={(map) => (mapRef.current = map)}
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={defaultCenter}
              zoom={6}
              options={mapOptions}
            >
              {filtered
                .filter((p) => inFR(p.lat, p.lng))
                .map((point) => (
                  <Marker
                    key={point.id}
                    position={{ lat: point.lat, lng: point.lng }}
                    onClick={() => setSelectedPoint(point)}
                    icon={{
                      url:
                        (point.category && categoryIcons[point.category]) ||
                        categoryIcons.default,
                      scaledSize: new window.google.maps.Size(40, 40),
                    }}
                    animation={window.google.maps.Animation.DROP}
                  />
                ))}

              {selectedPoint && inFR(selectedPoint.lat, selectedPoint.lng) && (
                <InfoWindow
                  position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
                  onCloseClick={() => setSelectedPoint(null)}
                >
                  <div>
                    <h3 className="font-bold">{selectedPoint.name}</h3>
                    <p>
                      {selectedPoint.formattedAddress ||
                        selectedPoint.address ||
                        [selectedPoint.address1, selectedPoint.address2]
                          .filter(Boolean)
                          .join(", ")}
                    </p>
                    <p>
                      {selectedPoint.postalCode
                        ? `${selectedPoint.postalCode} ${selectedPoint.city || ""}`
                        : selectedPoint.city}
                    </p>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </div>
        </div>
      </div>
    </>
  );
};


export default SalesPointsMap;